import type { ModalProps } from 'app/components/Modal'
import { wrap } from 'lodash'
import { debug } from 'lib/logging'

type ModalStatePayload = { isMinimized: boolean }
type ModalStateHandler = (payload: ModalStatePayload) => void

const modalStateHandlers = new Set<ModalStateHandler>()

// Store modal state in a stable object that survives HMR
// When HMR re-evaluates this module, `let` variables reset to undefined,
// but this object reference persists on window
type ModalState = {
  isModalOpen: boolean
  isNestedModalOpen: boolean
  options?: OptionsType
  nestedOptions?: OptionsType
  setContent?: (elem: React.ReactElement | undefined | null) => void
  setOptions?: (options: OptionsType | undefined) => void
  setTitle?: (title: string | React.ReactElement) => void
  setNestedTitle?: (title: string | React.ReactElement) => void
  setNestedContent?: (elem: React.ReactElement | undefined | null) => void
  setNestedOptions?: (options: OptionsType | undefined) => void
  setMainMinimizedState?: (value: boolean) => void
  setModalOpen?: (open: boolean) => void
  setNestedModalOpen?: (open: boolean) => void
}

declare global {
  interface Window {
    __modalState?: ModalState
  }
}

export const getModalState = (): ModalState => {
  if (typeof window !== `undefined`) {
    if (!window.__modalState) {
      window.__modalState = { isModalOpen: false, isNestedModalOpen: false }
    }
    return window.__modalState
  }
  // SSR fallback
  return { isModalOpen: false, isNestedModalOpen: false }
}

export type OptionsType = Omit<ModalProps, `open` | `children`>
/**
 * opens a modal at body level
 * @Param elem. The element to render in the modal
 * @Param options. The options to pass to the modal
 * @Param allowNesting. Default value is true. There are cases where a modal opens instead of another eg. Integration modal. In such cases, allowNesting should be false
 */
export function showModal(
  elem: React.ReactElement,
  options: Partial<OptionsType> = {},
  allowNesting: boolean | undefined = true,
) {
  const state = getModalState()
  debug(`modal`, `showModal ready?`, state.setContent != null)
  if (state.isModalOpen && allowNesting) {
    state.setNestedContent?.(elem)
    // Flip the flag here, not only from the shell's effect: the nested body's own mount
    // effects (a `setModalTitle` on open) run before the shell has re-rendered.
    state.isNestedModalOpen = true
    options.onClose = wrap(options.onClose, (func) => {
      state.isNestedModalOpen = false
      state.setNestedModalOpen?.(false)
      func?.()
    })
    state.nestedOptions = options as OptionsType
    state.setNestedOptions?.(options as OptionsType)
  } else {
    state.setContent?.(elem)
    options.onClose = wrap(options.onClose, (func) => {
      // Mirrors the nested branch: flip synchronously so a body's own mount effect
      // (a `setModalTitle` on close) never reads a stale `isModalOpen`/`isNestedModalOpen`.
      state.isModalOpen = false
      state.isNestedModalOpen = false
      state.setModalOpen?.(false)
      func?.()
    })
    state.options = options as OptionsType
    state.setOptions?.(options as OptionsType)
  }
}

export function closeModal() {
  const state = getModalState()
  if (state.isNestedModalOpen) {
    state.setNestedModalOpen?.(false)
    state.nestedOptions?.onClose?.()
  } else {
    state.isModalOpen = false
    state.isNestedModalOpen = false
    state.setModalOpen?.(false)
    state.options?.onClose?.()
  }
}

export function minimizeModal() {
  const state = getModalState()
  state.setMainMinimizedState?.(true)
  notifyModalStateChange(true)
}

export function maximizeModal() {
  const state = getModalState()
  state.setMainMinimizedState?.(false)
  notifyModalStateChange(false)
}

function notifyModalStateChange(isMinimized: boolean) {
  modalStateHandlers.forEach((handler) => handler({ isMinimized }))
}

export function onModalStateChange(handler: ModalStateHandler): () => void {
  modalStateHandlers.add(handler)
  return () => {
    modalStateHandlers.delete(handler)
  }
}

/** The modal on top owns its title, so a nested body never renames the one beneath it. */
export function setModalTitle(title: string | React.ReactElement) {
  const state = getModalState()
  if (state.isNestedModalOpen) state.setNestedTitle?.(title)
  else state.setTitle?.(title)
}
